import { useCallback, useEffect, useMemo, useState } from 'react';

import dotize from 'dotize';
import pick from 'lodash-es/pick';
import { BoxIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, Link } from 'react-router';
import { toast } from 'sonner';

import { callUpload } from '@/api/fetcher';
import { EditableText, EditableTextVariant } from '@/components/atoms/EditableText/EditableText';
import KeyValueRow from '@/components/atoms/KeyValueTable/KeyValueRow/KeyValueRow';
import { MODAL_MERGE_CONFLICTS } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDeskId } from '@/hooks/useDeskId';
import usePrompt from '@/hooks/usePrompt';
import { useBundles } from '@/modules/bundles/hooks/useBundles';
import {
  setDraftBundle,
  addContext,
  deleteContext,
  updateContext,
  revertDirty,
} from '@/modules/bundles/redux/actions';
import {
  selectBundle,
  selectBundleContext,
  selectBundleContextKeys,
  selectBundleKeysWithNested,
  selectIsBundleDirty,
} from '@/modules/bundles/redux/selectors';
import { findCommonKeys } from '@/modules/bundles/utils';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setBundleId } from '@/redux/session/actions';
import {
  selectAccountId,
  selectAccountSlug,
  selectFileUploadMetadata,
} from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { FIFTEEN_MB, HUNDRED_BYTES } from '@/util/constants';
import {
  preventClickThrough,
  getRestrictedNames,
  generateUniqueName,
  extractNames,
} from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { getEditableTextValidationSchema } from '@/util/validation/schema-builders';

import styles from './Bundle.module.scss';

const ONE_MEGABYTE = 1000000;

const Bundle = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchFilter, setSearchFilter] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const deskId = useDeskId();
  const { bundleId } = useParams();
  const draftBundle = useSelector(selectBundle);
  const location = useLocation();

  const { bundle, isSuccess, bundles, updateBundle, addBundle } = useBundles(deskId, bundleId);
  const context = useSelector(selectBundleContext);
  const contextKeys = useSelector(selectBundleContextKeys);
  const nestedKeys = useSelector(selectBundleKeysWithNested);
  const slug = useSelector(selectAccountSlug);
  const isDraft = location.pathname.includes('draft');
  const name = draftBundle?.name;
  const accountId = useSelector(selectAccountId);
  const metadata = useSelector(selectFileUploadMetadata);
  const isDirty = useSelector(selectIsBundleDirty);

  const uploadPath = `/www/media/${accountId}/upload`;
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'bundles', actions.WRITE)
  );

  useEffect(() => {
    if (bundleId) {
      dispatch(setBundleId(bundleId));
    }
    return () => {
      dispatch(setBundleId(null));
    };
  }, [dispatch, bundleId]);

  const onJsonAccepted = useCallback(
    (files) => {
      const reader = new FileReader();
      reader.onload = () => {
        const json = JSON.parse(reader.result as string);
        const commonKeys = findCommonKeys(json, context);
        if (commonKeys?.length === 0) {
          dispatch(
            setDraftBundle({
              ...draftBundle,
              context: { ...draftBundle?.context, ...dotize.convert(json) },
              dirty: true,
            })
          );
        } else {
          dispatch(pushModal(MODAL_MERGE_CONFLICTS, { conflicts: commonKeys, draftBundle, json }));
        }
      };
      reader.readAsText(files[0]);
    },
    [context, dispatch, draftBundle]
  );

  const { getInputProps, open } = useDropzone({
    accept: { 'application/json': ['.json'] },
    maxSize: ONE_MEGABYTE,
    onDropAccepted: (files) => onJsonAccepted(files),
  });

  useSegments(PageName.BUNDLE);

  useEffect(() => {
    if (bundle) {
      dispatch(
        setDraftBundle({ dirty: false, ...bundle, context: dotize.convert(bundle.context) })
      );
    }
    return () => {
      dispatch(revertDirty({ dirtyState: false }));
    };
  }, [dispatch, bundle]);

  useEffect(() => {
    if (isDraft && deskId && !draftBundle.name) {
      navigate(`/${slug}/environments/${deskId}/context_bundles`);
    }
  }, [deskId, draftBundle, isDraft, navigate, slug]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(event.target.value?.toLowerCase());
  };

  const handleAddContext = useCallback(
    (itemKey: string, itemValue: string, options?: Record<string, string | number>) => {
      const index = options?.index as number;
      if (itemKey.includes('.') && contextKeys.includes(itemKey.split('.')[0])) {
        setHasError(true);
        setErrorIndex(index || null);
        return;
      }
      if (contextKeys.includes(itemKey) || nestedKeys.includes(itemKey)) {
        setHasError(true);
        setErrorIndex(index || null);
        return;
      }
      dispatch(addContext({ itemKey, itemValue, dirty: isDraft ? true : undefined }));
      setHasError(false);
      setErrorIndex(null);
    },
    [contextKeys, dispatch, isDraft, nestedKeys]
  );

  const handleUpdateContext = useCallback(
    (
      oldKey: string,
      itemKey: string,
      itemValue: string,
      options?: Record<string, string | number>
    ) => {
      const { focus } = options;
      if (focus !== 'value' && (contextKeys.includes(itemKey) || nestedKeys.includes(itemKey))) {
        setHasError(true);
        return;
      }
      if (!hasError) {
        dispatch(updateContext({ itemKey, itemValue, oldKey }));
      }
    },
    [contextKeys, dispatch, hasError, nestedKeys]
  );

  const handleKeyBlur = useCallback(
    (keyRef: string, options?: Record<string, string | number>) => {
      const index = options?.index as number;
      if (contextKeys.includes(keyRef) || nestedKeys.includes(keyRef)) {
        setHasError(true);
        setErrorIndex(index);
        return;
      }
      setHasError(false);
      setErrorIndex(null);
    },
    [contextKeys, nestedKeys]
  );

  const handleDelete = useCallback(
    (itemKey) => {
      dispatch(deleteContext({ itemKey }));
    },
    [dispatch]
  );

  const handleFileUpload = async (
    newKey: string,
    oldKey: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    preventClickThrough(e);
    const file = e.target.files[0];
    if (file.size > FIFTEEN_MB) {
      toast.error(t('file_upload.fix_too_large', { 0: `${FIFTEEN_MB / 1000000}` }));

      return;
    }
    if (file.size < HUNDRED_BYTES) {
      toast.error(t('file_upload.fix_too_small', { 0: `${HUNDRED_BYTES / 1000}` }));
      return;
    }
    e.target.value = '';

    const formData = new FormData();
    formData.append('file', file);
    if (metadata) {
      formData.append('metadata', metadata);
    }
    callUpload(uploadPath, formData).then((resp) => {
      if (oldKey === '') {
        dispatch(addContext({ itemKey: newKey, itemValue: resp.url, dirty: true }));
      } else {
        dispatch(updateContext({ itemKey: newKey, itemValue: resp.url, oldKey: newKey }));
      }
    });
  };

  const filteredContextKeys = useMemo(
    () =>
      contextKeys.filter(
        (key) =>
          key?.toLowerCase().includes(searchFilter) ||
          context[key]?.toLowerCase().includes(searchFilter)
      ),
    [context, contextKeys, searchFilter]
  );
  const isReadOnly = !canWrite;

  const redirectToBundles = useCallback(() => {
    navigate(`/${slug}/environments/${deskId}/context_bundles`);
  }, [deskId, navigate, slug]);

  const handleSubmit = useCallback(() => {
    setIsLoading(true);
    if (isDraft) {
      addBundle(
        {
          ...pick(draftBundle, ['desk_id', 'description']),
          name: generateUniqueName(draftBundle.name, extractNames(bundles)),

          context: dotize.backward(draftBundle.context),
        },
        {
          onSuccess: () => {
            redirectToBundles();
          },
        }
      );
    } else {
      updateBundle(
        { ...draftBundle, context: dotize.backward(draftBundle.context) },
        {
          onSuccess: () => {
            redirectToBundles();
          },

          onSettled: () => {
            setIsLoading(false);
            dispatch(revertDirty({ dirtyState: false }));
          },
        }
      );
    }
  }, [addBundle, bundles, dispatch, draftBundle, isDraft, redirectToBundles, updateBundle]);

  const handleNameSubmit = useCallback(
    (text: string) => {
      dispatch(setDraftBundle({ ...draftBundle, name: text, dirty: true }));
    },
    [dispatch, draftBundle]
  );

  usePrompt(isDirty, t('common.header_prompt'), undefined, handleSubmit);

  // Filters out the current bundle name from the list of restricted values
  const restrictedValues = getRestrictedNames(bundles, bundle?.name);

  const validationSchema = getEditableTextValidationSchema(
    LENGTH_XS,
    restrictedValues,
    t('environment.environment_name')
  );

  const editableTextProps = {
    defaultValue: name,
    onSubmit: handleNameSubmit,
    variant: 'body-semi-bold' as EditableTextVariant,
    canEdit: canWrite,
    validationSchema,
  };

  return (
    <Layout>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/environments/${deskId}/context_bundles`}>
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <BoxIcon size={16} className="text-muted-foreground" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        {t('common.environments')}
                      </div>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/environments/${deskId}/context_bundles`}>
                      {t('common.context_bundles')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="border-0 p-0">
                    <EditableText<typeof validationSchema> {...editableTextProps} />
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        actions={
          <PageSubHeaderActions>
            <Button variant="outline-neutral" onClick={redirectToBundles}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!isDirty || !canWrite} isLoading={isLoading}>
              {t('common.save_and_close')}
            </Button>
          </PageSubHeaderActions>
        }
      />
      <PageContentWrapper readOnly={!canWrite}>
        <div className={styles.container}>
          {(isSuccess || isDraft) && (
            <>
              <div className={styles.input}>
                <Input
                  placeholder={t('bundle.search')}
                  size="sm"
                  onChange={handleSearchChange}
                  value={searchFilter}
                />
                <div>
                  <input {...getInputProps()} />
                  <Button variant="ghost" onClick={open} disabled={isReadOnly}>
                    {t('bundles.upload')}
                  </Button>
                </div>
              </div>
              <div className={styles.innerTitle}>
                <span>{t('bundles.item_list')}</span>
              </div>
              <div className={styles.wrapper}>
                {filteredContextKeys.map((x, i) => (
                  <KeyValueRow
                    key={x}
                    itemKey={x}
                    itemValue={context[x]}
                    index={i}
                    keys={contextKeys}
                    onAdd={handleAddContext}
                    onUpdate={handleUpdateContext}
                    onKeyBlur={handleKeyBlur}
                    onDelete={handleDelete}
                    error={hasError && errorIndex === i}
                    readOnly={!canWrite}
                    onUpload={handleFileUpload}
                  />
                ))}
                <KeyValueRow
                  key={contextKeys.length}
                  itemKey=""
                  itemValue=""
                  index={contextKeys.length}
                  keys={contextKeys}
                  onAdd={handleAddContext}
                  onUpdate={handleUpdateContext}
                  onKeyBlur={handleKeyBlur}
                  onDelete={handleDelete}
                  showDelete={false}
                  error={hasError && errorIndex === contextKeys.length}
                  readOnly={!canWrite}
                  onUpload={handleFileUpload}
                />
              </div>
            </>
          )}
        </div>
      </PageContentWrapper>
    </Layout>
  );
};

export default Bundle;
